import * as ol from 'ol';
import { Feature, View } from 'ol';
import { GeoJSON } from 'ol/format';
import Map from 'ol/Map';
import { OSM, TileWMS, XYZ } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import { fromLonLat } from 'ol/proj';
import { getCenter } from 'ol/extent';
import MapUtils from '@vendor/js/map-utils';
import { get, set } from 'lodash';
import { LayersAndBaseInterfaceElement } from '@vendor/libs/maps/interface-elements/layers-and-base';
import { LegendInterfaceElement } from '@vendor/libs/maps/interface-elements/legend';
export var CustomMapBaseType;
(function (CustomMapBaseType) {
    CustomMapBaseType["OSM"] = "osm";
    CustomMapBaseType["THUNDERFOREST"] = "thunderforest";
    CustomMapBaseType["GMAPS_HYBRID"] = "gmaps_hybrid";
    CustomMapBaseType["GEOPORTAL_HYBRID"] = "geoportal_hybrid";
})(CustomMapBaseType || (CustomMapBaseType = {}));
class CustomMap {
    constructor(selector) {
        this.features = {};
        this.interactions = {};
        this.interfaceElements = {};
        this.loadingCount = 0;
        this.ol = new Map({
            target: selector,
            view: new View({
                center: [0, 0],
                zoom: 2,
            }),
        });
        this.selector = selector;
        CustomMap.maps[selector] = this;
        this.addBase();
        // todo
        // allow modification of these
        this.addInterfaceElement('layers-and-base', new LayersAndBaseInterfaceElement());
        this.addInterfaceElement('legend', new LegendInterfaceElement());
        this.trigger('init', this);
    }
    addBase() {
        this.currentBaseType = CustomMap.getFromStorage('base.source');
        if (!this.currentBaseType) {
            this.currentBaseType = CustomMapBaseType.OSM;
        }
        this.baseLayer = new TileLayer({
            source: this.getBaseSource(this.currentBaseType),
        });
        this.ol.addLayer(this.baseLayer);
    }
    changeBase(type) {
        const source = this.getBaseSource(type);
        this.currentBaseType = type;
        CustomMap.saveInStorage('base.source', type);
        if (this.baseLayer) {
            this.baseLayer.setSource(source);
        }
    }
    setView(center, zoom = null) {
        let view = this.ol.getView();
        view.setCenter(fromLonLat(center));
        if (zoom) {
            view.setZoom(zoom);
        }
    }
    fitTo(geojson, options = {}) {
        options = {
            ...{
                padding: [24, 24, 24, 24],
                maxZoom: undefined,
            },
            ...options,
        };
        const feature = MapUtils.geojsonToFeature(geojson);
        if (feature) {
            const extent = feature.getGeometry()?.getExtent() ?? null;
            if (extent) {
                this.ol
                    .getView()
                    .fit(extent, { padding: options.padding, maxZoom: options.maxZoom });
            }
        }
    }
    addInteraction(id, interaction) {
        interaction._handle(this, id);
        this.interactions[id] = interaction;
    }
    hasInteraction(id) {
        return typeof this.interactions[id] != 'undefined';
    }
    removeInteraction(id) {
        if (this.hasInteraction(id)) {
            this.interactions[id]._handleRemove();
            delete this.interactions[id];
        }
    }
    addFeatures(id, features) {
        features._handle(this, id);
        this.features[id] = features;
    }
    removeFeatures(id) {
        this.features[id]._handleRemove();
        delete this.features[id];
    }
    getFeatures(id) {
        return this.features[id] ?? null;
    }
    addInterfaceElement(id, interfaceElement) {
        interfaceElement._handle(this, id);
        this.interfaceElements[id] = interfaceElement;
    }
    hasInterfaceElement(id) {
        return typeof this.interfaceElements[id] != 'undefined';
    }
    removeInterfaceElement(id) {
        if (this.hasInterfaceElement(id)) {
            this.interfaceElements[id]._handleRemove();
            delete this.interfaceElements[id];
        }
    }
    setLoading(state = true) {
        if (state) {
            this.loadingCount++;
        }
        else {
            if (this.loadingCount > 0) {
                this.loadingCount--;
            }
        }
        if (this.loadingCount) {
            $(`#${this.selector}`).find('[data-app-map_loading]').show();
        }
        else {
            $(`#${this.selector}`).find('[data-app-map_loading]').hide();
        }
    }
    getBaseSource(type) {
        switch (type) {
            case CustomMapBaseType.THUNDERFOREST:
                return new XYZ({
                    url: 'https://tile.thunderforest.com/landscape/{z}/{x}/{y}.png?apikey=723ab88c4e634adca07e19a0d8f2633e',
                });
            case CustomMapBaseType.GMAPS_HYBRID:
                return new XYZ({
                    attributions: [],
                    url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                    maxZoom: 21,
                });
            case CustomMapBaseType.GEOPORTAL_HYBRID:
                return new TileWMS({
                    url: 'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/StandardResolution',
                    params: {
                        LAYERS: 'Raster',
                        FORMAT: 'image/png',
                        srs: 'EPSG:2180',
                    },
                    serverType: 'geoserver',
                });
            case CustomMapBaseType.OSM:
            default:
                return new OSM({
                    attributions: [],
                });
        }
    }
    trigger(event, data) {
        if (CustomMap.eventCallbacks &&
            CustomMap.eventCallbacks[this.selector] &&
            CustomMap.eventCallbacks[this.selector][event]) {
            for (const callback of CustomMap.eventCallbacks[this.selector][event]) {
                callback(data);
            }
        }
    }
    static getMap(selector) {
        return CustomMap.maps[selector];
    }
    static getFromStorage(key, mapId) {
        const localStorageKey = `e-pszczola.custom-map.${mapId ? `${mapId}.` : ''}storage`;
        const data = localStorage.getItem(localStorageKey);
        if (!data) {
            return null;
        }
        try {
            const parsed = JSON.parse(data);
            if (typeof parsed != 'object') {
                return null;
            }
            return get(parsed, key, null);
        }
        catch (err) {
            return null;
        }
    }
    static on(mapId, event, callback) {
        if (typeof this.eventCallbacks == 'undefined') {
            this.eventCallbacks = {};
        }
        if (typeof this.eventCallbacks[mapId] == 'undefined') {
            this.eventCallbacks[mapId] = {};
        }
        if (typeof this.eventCallbacks[mapId][event] == 'undefined') {
            this.eventCallbacks[mapId][event] = [];
        }
        this.eventCallbacks[mapId][event] = [
            ...this.eventCallbacks[mapId][event],
            callback,
        ];
    }
    static saveInStorage(key, value, mapId) {
        const localStorageKey = `e-pszczola.custom-map.${mapId ? `${mapId}.` : ''}storage`;
        let data = localStorage.getItem(localStorageKey);
        let parsed;
        try {
            parsed = JSON.parse(data);
        }
        catch (err) {
            parsed = {};
        }
        if (typeof parsed != 'object' || parsed === null) {
            parsed = {};
        }
        parsed = set(parsed, key, value);
        localStorage.setItem(localStorageKey, JSON.stringify(parsed));
    }
}
CustomMap.maps = [];
// Prototypes
Feature.prototype.getCenter = function () {
    return getCenter(this.getGeometry().getExtent());
};
export { ol, GeoJSON, CustomMap };
